/**
 * ConfigPage.jsx — Validator Config
 *
 * Sections:
 *   1. App Profiles  — initialize, capture, and manage per-app+platform configs
 *   2. SSL Proxying  — domain exclude list for the MITM proxy
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import {
  getAllApps, getActiveSelection, setActiveSelection,
  saveCustomApp, updateProfileField,
  getInitializedProfiles, uninitializeProfile,
  getAppSslExclusions, setAppSslExclusions,
  BUILTIN_APPS,
} from '../config/appProfiles';
import { captureSession } from '../utils/captureSession';
import { useTraffic } from '../context/TrafficContext';
import { confirmDelete } from '../utils/confirmDelete';
import { getPacks, refreshAppPacks, subscribeAppPacks, deriveScreens } from '../api/appPacks';
import MetaPlacementReportPanel from '../components/MetaPlacementReportPanel';
import { buildPackSavePayload } from '../utils/appPackPayload';
import DeviceCapturePanel from '../components/DeviceCapturePanel';

// ─── tiny helpers ─────────────────────────────────────────────────────────────

const SECTION_STYLE = {
  border: '1px solid var(--line)',
  borderRadius: 10,
  overflow: 'hidden',
  marginBottom: 24,
};
const SECTION_HEAD = {
  background: 'var(--card)',
  padding: '12px 16px',
  borderBottom: '1px solid var(--line)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const SECTION_BODY = { padding: '16px' };

function SectionHeader({ title, badge, children }) {
  return (
    <div style={SECTION_HEAD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
        {badge && (
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10,
            background: 'var(--bg)', border: '1px solid var(--line)',
            color: 'var(--muted)',
          }}>{badge}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function Tag({ children, color = 'var(--muted)' }) {
  return (
    <span style={{
      fontSize: 11, padding: '1px 7px', borderRadius: 6,
      border: `1px solid ${color}40`, color, background: `${color}15`,
      fontFamily: 'monospace', fontWeight: 600,
    }}>{children}</span>
  );
}

/* ── App Profile Section ─────────────────────────────────────────────────── */

// Platform display names used in the wizard
const PLATFORM_OPTIONS = [
  { id: 'iosmob',   name: 'iOS Mobile' },
  { id: 'droidmob', name: 'Android Mobile' },
  { id: 'iostab',   name: 'iOS Tablet' },
  { id: 'droidtab', name: 'Android Tablet' },
];

function AppProfileSection() {
  const navigate = useNavigate();

  const refresh = () => {
    setProfiles(getInitializedProfiles());
    setSel(getActiveSelection());
  };

  const [profiles, setProfiles] = useState(() => getInitializedProfiles());
  const [{ appId, profileId }, setSel] = useState(() => getActiveSelection());

  // Wizard state — null = closed
  const [wizard, setWizard] = useState(null);
  // wizard = { step: 1|2|3, appId, profileId, fields: {}, customApp: bool }

  const activeComposite = `${appId}/${profileId}`;

  const inp = (style = {}) => ({
    padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line)',
    background: 'var(--card)', color: 'var(--ink)', fontSize: 13,
    width: '100%', ...style,
  });

  /* ── Activate a card ── */
  const handleActivate = (composite) => {
    const [aId, pId] = composite.split('/');
    setActiveSelection(aId, pId);
    setSel({ appId: aId, profileId: pId });
  };

  /* ── Remove an initialized profile ── */
  const handleRemove = (composite) => {
    const [aId, pId] = composite.split('/');
    const prof = profiles.find(p => p.id === composite);
    const name = prof?.name || composite;
    // Strong double-confirm: permanently deletes the captured config.
    if (!confirmDelete(name, {
      strong: true,
      detail: 'This permanently removes the captured profile (Prebid IDs, Amazon slots, banner sizes, rules). This cannot be undone.',
    })) return;
    uninitializeProfile(aId, pId);
    // If active was the removed one, fall back to first remaining or generic
    if (activeComposite === composite) {
      const remaining = getInitializedProfiles().filter(p => p.id !== composite);
      if (remaining.length > 0) {
        const [rA, rP] = remaining[0].id.split('/');
        setActiveSelection(rA, rP);
      } else {
        setActiveSelection('generic', 'default');
      }
      setSel(getActiveSelection());
    }
    refresh();
  };

  /* ── Wizard helpers ── */
  const openWizard = () => setWizard({ step: 1, appId: null, profileId: null, fields: {}, customApp: false });
  const closeWizard = () => setWizard(null);

  const wizardNext = (updates) => setWizard(w => ({ ...w, ...updates, step: w.step + 1 }));

  /* ── Profile card ── */
  const ProfileCard = ({ prof }) => {
    const isActive = prof.id === activeComposite;
    const prebidCount = prof.prebidMappings ? Object.keys(prof.prebidMappings).length : 0;
    const slotCount   = prof.amazonSlotIds  ? Object.keys(prof.amazonSlotIds).length  : 0;
    const sizeCount   = prof.allowedBannerSizes?.length || 0;

    const phases = [
      prof.hasPrebid      === true && 'Prebid',
      prof.hasAPS         === true && 'APS',
      prof.hasSpringServe === true && 'SpringServe',
      prof.hasIMA         === true && 'IMA',
    ].filter(Boolean);

    return (
      <div
        onClick={() => handleActivate(prof.id)}
        style={{
          border: `2px solid ${isActive ? 'var(--primary)' : 'var(--line)'}`,
          borderRadius: 10, padding: '14px 16px', marginBottom: 10,
          background: isActive ? 'var(--primary)15' : 'var(--card)',
          cursor: 'pointer', position: 'relative',
          transition: 'border-color 0.15s',
        }}
      >
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>{prof.name}</span>
            {isActive && (
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 10,
                background: 'var(--primary)', color: '#fff', fontWeight: 700,
              }}>ACTIVE</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setEditingCard(editingCard === prof.id ? null : prof.id)}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--line)', background: editingCard === prof.id ? 'var(--primary)' : 'transparent', color: editingCard === prof.id ? '#fff' : 'var(--ink)', cursor: 'pointer' }}>
              ✏ Edit
            </button>
            <button onClick={() => handleRemove(prof.id)} title="Remove"
              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--line)', background: 'transparent', color: 'var(--error-text, #dc2626)', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 12, color: 'var(--muted)' }}>
          {prof.adUnitPrefix && (
            <span><code style={{ fontSize: 11 }}>{prof.adUnitPrefix}</code></span>
          )}
          {phases.length > 0 && <span>{phases.join(' · ')}</span>}
          {prof.publisherId && <span>Publisher: {prof.publisherId}</span>}
          {sizeCount > 0   && <span>{sizeCount} sizes</span>}
          {prebidCount > 0 && <span>{prebidCount} Prebid IDs</span>}
          {slotCount > 0   && <span>{slotCount} APS slots</span>}
          {prebidCount === 0 && prof.hasPrebid === true && (
            <span style={{ color: '#854f0b' }}>⚠ Prebid IDs not configured</span>
          )}
        </div>
      </div>
    );
  };

  /* captureTick removed — steps 3 & 4 now run in MainApp (where WebSocket lives) */

  /* ── Wizard UI ── */
  const WizardModal = () => {
    if (!wizard) return null;
    const allApps = getAllApps();

    // ── Step 1: pick app ────────────────────────────────────────────────────
    if (wizard.step === 1) {
      const builtinApps = Object.values(BUILTIN_APPS).filter(a => a.id !== 'generic');
      return (
        <WizardOverlay title="Initialize App — Pick App" onClose={() => { captureSession.stop(); closeWizard(); }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            Each app + platform is set up once. The tool will capture everything from live traffic.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {builtinApps.map(a => (
              <button key={a.id} onClick={() => wizardNext({ appId: a.id, customApp: false })}
                style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg)', cursor: 'pointer', fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{a.name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{a.description || a.adUnitPrefix}</div>
              </button>
            ))}
            <button onClick={() => wizardNext({ customApp: true })}
              style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 8, border: '1px dashed var(--line)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--muted)' }}>
              + Other app (any brand not listed above)
            </button>
          </div>
        </WizardOverlay>
      );
    }

    // ── Step 1b: custom app — just a name, everything else from capture ──────
    if (wizard.step === 2 && wizard.customApp) {
      const nameVal = wizard.fields._customAppName || '';
      const autoId  = nameVal.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      return (
        <WizardOverlay title="New App" onClose={() => { captureSession.stop(); closeWizard(); }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.6 }}>
            Just give the app a name. The ad unit prefix, Prebid IDs, Amazon slots, and banner sizes
            will all be captured automatically from live traffic in the next step.
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>App name</label>
            <input
              value={nameVal}
              onChange={e => setWizard(w => ({
                ...w,
                fields: {
                  ...w.fields,
                  _customAppName: e.target.value,
                  _customAppId: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
                }
              }))}
              placeholder="e.g. TMZ, ABC News, ESPN"
              autoFocus
              style={{ ...inp(), fontSize: 15, padding: '10px 12px' }}
            />
            {autoId && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
                App ID: <code style={{ color: 'var(--ink)' }}>{autoId}</code>
              </div>
            )}
          </div>
          <WizardNav
            onBack={() => setWizard(w => ({ ...w, step: 1 }))}
            onNext={() => setWizard(w => ({ ...w, step: 2.5 }))}
            nextDisabled={!nameVal.trim()}
          />
        </WizardOverlay>
      );
    }

    // ── Step 2: pick platform ────────────────────────────────────────────────
    if ((wizard.step === 2 && !wizard.customApp) || wizard.step === 2.5) {
      const effAppId  = wizard.customApp ? wizard.fields._customAppId : wizard.appId;
      const appName   = wizard.customApp ? wizard.fields._customAppName : (allApps[wizard.appId]?.name || wizard.appId);
      const unavailable = new Set(getInitializedProfiles().filter(p => p.appId === effAppId).map(p => p.profileId));
      const available = PLATFORM_OPTIONS.filter(p => !unavailable.has(p.id));

      const startCapture = (platform) => {
        // For custom apps, register a minimal app entry before capture so
        // it appears in getAllApps() when MainApp's review runs.
        if (wizard.customApp && wizard.fields._customAppId) {
          try {
            saveCustomApp({
              id: wizard.fields._customAppId, name: wizard.fields._customAppName,
              description: '', adUnitPrefix: null,
              hasPrebid: null, hasAPS: null, hasAPSVideo: null, hasSpringServe: null, hasIMA: null,
              taxonomy: null,
              profiles: { [platform.id]: { id: platform.id, name: platform.name, publisherId: null, bundleId: null, allowedBannerSizes: null, requiredCustParams: [], expectedBidders: null, prebidMappings: null, amazonSlotIds: null } },
            });
          } catch {}
        }
        captureSession.start(effAppId, platform.id, { appName, profileName: platform.name });
        closeWizard();
        navigate('/');   // go to the Inspector — that's where the WebSocket lives
      };

      return (
        <WizardOverlay title="Select Platform" onClose={() => { captureSession.stop(); closeWizard(); }}>
          {available.length === 0
            ? <div style={{ fontSize: 13, color: 'var(--muted)', padding: 12 }}>All platforms for this app are already initialized.</div>
            : <>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                  After selecting a platform, you'll be taken to the Inspector. Connect your device and scroll through every page layout — the tool captures everything live.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {available.map(p => (
                    <button key={p.id} onClick={() => startCapture(p)}
                      style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </>
          }
          <WizardNav onBack={() => setWizard(w => ({ ...w, step: wizard.customApp ? 2 : 1 }))} onNext={null} />
        </WizardOverlay>
      );
    }

    // Steps 3 (capture) and 4 (review) run in MainApp.js where the WebSocket lives.
    // Navigating to '/' above closes this wizard and hands off to AutoDetectBanner.
    return null;
  };

  /* ── Manual Edit Panel (shown inside an expanded profile card) ── */
  const [editingCard, setEditingCard] = useState(null);  // composite id or null

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title="🏷 Configured App Profiles" badge={`${profiles.length} profile${profiles.length !== 1 ? 's' : ''}`}>
        <button
          onClick={openWizard}
          style={{ fontSize: 12, padding: '5px 14px', borderRadius: 7, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
        >
          + Initialize New App
        </button>
      </SectionHeader>

      <div style={SECTION_BODY}>
        {profiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📱</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)', marginBottom: 6 }}>No profiles configured yet</div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>
              Initialize your first app to enable client-specific validation rules.
              Profiles are saved permanently — set up once, reuse every session.
            </div>
            <button onClick={openWizard} style={{ fontSize: 13, padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              + Initialize First App
            </button>
          </div>
        ) : (
          <>
            {profiles.map(p => (
              <React.Fragment key={p.id}>
                <ProfileCard prof={p} />
                {editingCard === p.id && <ManualEditPanel prof={p} onClose={() => setEditingCard(null)} onSaved={refresh} />}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      <WizardModal />
    </div>
  );
}

/* ── Manual Edit Panel ────────────────────────────────────────────────────── */

// Blank rule template for the "Add Rule" form
const BLANK_RULE = { key: '', label: '', required: true, severity: 'fail', valMode: 'any', values: '', pattern: '', pages: '', multi: false };

// Friendly display names for known page codes (IU path segments) and
// cust_params pageType values. Anything not listed is title-cased on the fly.
// The actual page list shown in the editor is derived PER PROFILE (see
// buildPageTypes) from the profile's own taxonomy + the pages its rules
// reference — so e.g. one app never shows another app's page types.
const PAGE_LABELS = {
  all:             'All pages',
  // App A (IU segment based)
  hp:              'Home',
  fyp:             'For You',
  viddetail:       'Video detail',
  vidhme:          'Video home',
  art:             'Article',
  sf:              'Special feature',
  slide:           'Slideshow / Gallery',
  // App B (cust_params pageType based)
  'special-event': 'World Cup / Special event',
  'world-cup':     'World Cup / Special event',
  event:           'Event / Matchup',
  story:           'Story / Article',
  team:            'Team',
  athlete:         'Player / Athlete',
  watch:           'Watch / Live TV',
  sfvod:           'SF VOD',
};

// Page codes that mean the same group (normalised to the first form).
const PAGE_ALIASES = { 'world-cup': 'special-event' };

function prettyPageName(code) {
  const low = String(code || '').toLowerCase();
  if (PAGE_LABELS[low]) return PAGE_LABELS[low];
  return low.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || code;
}

// Normalise a raw page token to its canonical group id.
function canonPage(code) {
  const low = String(code || '').trim().toLowerCase();
  return PAGE_ALIASES[low] || low;
}

// Build the page-type options for a single profile:
//   1. 'All pages' always first
//   2. the profile's taxonomy pages (flatPages + catPages)
//   3. any pages already referenced by the profile's rules (captured/imported)
// This keeps the list scoped to what the app actually has.
function buildPageTypes(prof, draftRules = []) {
  const out  = [{ id: 'all', name: 'All pages' }];
  const seen = new Set(['all']);
  const add = (raw) => {
    const id = canonPage(raw);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push({ id, name: prettyPageName(id) });
  };
  const tax = prof?.taxonomy || {};
  [...(tax.flatPages || []), ...(tax.catPages || [])].forEach(add);
  draftRules.forEach(r => {
    const raw = typeof r.pages === 'string'
      ? r.pages.split(',')
      : (Array.isArray(r.pages) ? r.pages : []);
    raw.map(s => String(s).trim()).filter(Boolean).forEach(add);
  });
  return out;
}

function ManualEditPanel({ prof, onClose, onSaved }) {
  const compositeId = prof.id; // 'generic/iosmob'
  const [draft, setDraft] = useState({
    publisherId:        prof.publisherId        || '',
    bundleId:           prof.bundleId           || '',
    allowedBannerSizes: (prof.allowedBannerSizes || []).join(', '),
    prebidMappings:     prof.prebidMappings     || {},
    amazonSlotIds:      prof.amazonSlotIds      || {},
    custParamRules:     (prof.custParamRules    || []).map(r => ({
      ...r,
      valMode: r.values?.length > 0 ? 'enum' : r.pattern ? 'regex' : 'any',
      values:  (r.values || []).join(', '),
      pattern: r.pattern || '',
      pages:   (r.pages  || []).join(', '),
      multi:   r.multi   || false,
    })),
  });
  const [newPrebidKey, setNewPrebidKey] = useState('');
  const [newPrebidId,  setNewPrebidId]  = useState('');
  const [newSlotUuid,  setNewSlotUuid]  = useState('');
  const [newSlotName,  setNewSlotName]  = useState('');
  const [newSlotSizes, setNewSlotSizes] = useState('');
  const [saved, setSaved] = useState(false);
  // ── Fill gaps from the current session ────────────────────────────────────
  // Replays this session's already-captured requests through the capture engine
  // and merges only the MISSING fields into the draft — never overwriting values
  // the profile already has. Works on any route because it reads the persisted
  // TrafficContext rows rather than depending on a live WebSocket (which only
  // runs on the Dashboard route).
  const { liveTrafficGroups } = useTraffic();
  const sessionRows = useMemo(
    () => Object.values(liveTrafficGroups || {}).flat(),
    [liveTrafficGroups]
  );
  const [fillMsg, setFillMsg] = useState('');

  function fillGaps() {
    if (!sessionRows.length) { setFillMsg('No captured traffic in this session — capture on the Dashboard first.'); return; }
    // Replay the session through a fresh capture pass scoped to this profile.
    const wasActive = captureSession.isActive;
    captureSession.start(prof.appId, prof.profileId, { appName: prof.appName, profileName: prof.profileName });
    sessionRows.forEach(r => captureSession.addEntry(r));
    const summary = captureSession.getSummary();
    captureSession.stop();
    if (wasActive) { /* don't resurrect a wizard capture — leave stopped */ }

    setDraft(d => {
      const filled = [];
      const next = { ...d };
      if (!d.publisherId && summary.publisherIds?.[0]) { next.publisherId = summary.publisherIds[0]; filled.push('publisher ID'); }
      if (!d.bundleId && summary.bundleIds?.[0])       { next.bundleId    = summary.bundleIds[0];    filled.push('bundle ID'); }
      const existingSizes = d.allowedBannerSizes.split(',').map(s => s.trim()).filter(Boolean);
      const newSizes = (summary.allSizes || []).filter(s => !existingSizes.includes(s));
      if (newSizes.length) { next.allowedBannerSizes = [...existingSizes, ...newSizes].join(', '); filled.push(`${newSizes.length} size(s)`); }
      const addedPrebid = Object.keys(summary.prebidMappings || {}).filter(k => !(k in d.prebidMappings));
      if (addedPrebid.length) { next.prebidMappings = { ...summary.prebidMappings, ...d.prebidMappings }; filled.push(`${addedPrebid.length} Prebid ID(s)`); }
      const mergedSlots = { ...d.amazonSlotIds };
      let addedSlots = 0, backfilledSizes = 0;
      Object.entries(summary.amazonSlots || {}).forEach(([uuid, info]) => {
        const capturedSizes = info.sizes || [];
        const existing = d.amazonSlotIds[uuid];
        if (!existing) {
          mergedSlots[uuid] = { name: info.label || (uuid.slice(0, 8) + '…'), sizes: capturedSizes };
          addedSlots++;
        } else {
          // Backfill: union any newly-captured sizes into an existing slot, and
          // fill an empty label — never lose what's already there.
          const union = [...new Set([...(existing.sizes || []), ...capturedSizes])];
          const gainedSizes = union.length > (existing.sizes || []).length;
          const name = existing.name && !existing.name.endsWith('…') ? existing.name : (info.label || existing.name);
          if (gainedSizes || name !== existing.name) {
            mergedSlots[uuid] = { ...existing, name, sizes: union };
            if (gainedSizes) backfilledSizes++;
          }
        }
      });
      if (addedSlots || backfilledSizes) {
        next.amazonSlotIds = mergedSlots;
        if (addedSlots)      filled.push(`${addedSlots} APS slot(s)`);
        if (backfilledSizes) filled.push(`sizes for ${backfilledSizes} APS slot(s)`);
      }

      setFillMsg(filled.length
        ? `Filled ${filled.join(', ')} from ${summary.reqCount} requests — review below and click Save.`
        : `Scanned ${summary.reqCount} requests — no new values found (everything already set, or this session has no ${prof.profileName} traffic).`);
      return next;
    });
  }

  const [showImport, setShowImport] = useState(false);
  const [importMode, setImportMode] = useState('kvp'); // 'kvp' | 'json'
  const [importText, setImportText] = useState('');
  const [importPage, setImportPage] = useState('all');
  const [importMsg, setImportMsg] = useState('');
  // Which rule (by index) is expanded for editing, and which have Advanced open.
  const [expandedRule, setExpandedRule] = useState(null);
  const [advOpen, setAdvOpen] = useState({});

  // Page-type options scoped to THIS profile (taxonomy + pages its rules use).
  const pageTypes = useMemo(
    () => buildPageTypes(prof, draft.custParamRules),
    [prof, draft.custParamRules]
  );

  const inp = (s = {}) => ({ padding: '6px 9px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)', fontSize: 12, fontFamily: 'monospace', ...s });
  const sel = (s = {}) => ({ ...inp(s), fontFamily: 'inherit', cursor: 'pointer' });

  function serializeRules(rules) {
    return rules.map(r => {
      const out = {
        key:      r.key.trim(),
        label:    r.label.trim() || r.key.trim(),
        required: r.required !== false,
        severity: r.severity || 'fail',
      };
      if (r.valMode === 'enum' && r.values)   out.values  = r.values.split(',').map(v => v.trim()).filter(Boolean);
      if (r.valMode === 'regex' && r.pattern) out.pattern = r.pattern.trim();
      if (r.multi && r.valMode !== 'any')     out.multi   = true;
      const pgs = r.pages ? r.pages.split(',').map(p => p.trim()).filter(Boolean) : [];
      if (pgs.length) out.pages = pgs;
      return out;
    }).filter(r => r.key);
  }

  function save() {
    const fields = {};
    if (draft.publisherId) fields.publisherId = draft.publisherId;
    if (draft.bundleId)    fields.bundleId    = draft.bundleId;
    const sizes = draft.allowedBannerSizes.split(',').map(s => s.trim()).filter(Boolean);
    if (sizes.length) fields.allowedBannerSizes = sizes;
    if (Object.keys(draft.prebidMappings).length) fields.prebidMappings = draft.prebidMappings;
    if (Object.keys(draft.amazonSlotIds).length)  fields.amazonSlotIds  = draft.amazonSlotIds;
    fields.custParamRules = serializeRules(draft.custParamRules);
    updateProfileField(compositeId, fields);
    setSaved(true);
    onSaved();
    setTimeout(() => setSaved(false), 2000);
  }

  function addPrebid() {
    if (!newPrebidKey.trim() || !newPrebidId.trim()) return;
    setDraft(d => ({ ...d, prebidMappings: { ...d.prebidMappings, [newPrebidKey.trim().toLowerCase()]: newPrebidId.trim() } }));
    setNewPrebidKey(''); setNewPrebidId('');
  }

  function addSlot() {
    if (!newSlotUuid.trim()) return;
    const sizes = newSlotSizes.split(',').map(s => s.trim()).filter(Boolean);
    setDraft(d => ({ ...d, amazonSlotIds: { ...d.amazonSlotIds, [newSlotUuid.trim()]: { name: newSlotName || newSlotUuid.slice(0,8), sizes } } }));
    setNewSlotUuid(''); setNewSlotName(''); setNewSlotSizes('');
  }

  function addRule() {
    const newIdx = draft.custParamRules.length;
    setDraft(d => ({ ...d, custParamRules: [...d.custParamRules, { ...BLANK_RULE }] }));
    setExpandedRule(newIdx);   // open the new rule for editing immediately
    setAdvOpen(a => ({ ...a, [newIdx]: false }));
  }

  function updateRule(idx, patch) {
    setDraft(d => {
      const rules = [...d.custParamRules];
      rules[idx] = { ...rules[idx], ...patch };
      return { ...d, custParamRules: rules };
    });
  }

  function removeRule(idx) {
    const rule = draft.custParamRules[idx];
    const label = rule?.key ? `the "${rule.key}" rule` : 'this rule';
    if (!confirmDelete(label, { detail: 'Removed when you Save. Click Cancel to keep it.' })) return;
    setDraft(d => ({ ...d, custParamRules: d.custParamRules.filter((_, i) => i !== idx) }));
    setExpandedRule(null);   // indices shift after removal — collapse to stay safe
  }

  // Which page-type group a rule belongs to (first page token, or 'all')
  function ruleGroupId(rule) {
    const first = (rule.pages || '').split(',').map(s => s.trim()).filter(Boolean)[0];
    if (!first) return 'all';
    return canonPage(first);
  }

  // Import: paste a captured cust_params string (key=val&key2=val2…) and turn
  // each key into a draft rule. Values become an enum; multi-valued (comma)
  // values get the multi flag. Scoped to the selected page type.
  function importKvp() {
    const raw = importText.trim();
    if (!raw) { setImportMsg('Paste a cust_params string first'); return; }
    // Accept full URLs, "cust_params=..." or a bare key=val&key=val string
    let kv = raw;
    const cpMatch = raw.match(/cust_params=([^&\s]+)/);
    if (cpMatch) { try { kv = decodeURIComponent(cpMatch[1]); } catch { kv = cpMatch[1]; } }
    const pages = importPage === 'all' ? '' : importPage;
    const existingKeys = new Set(
      draft.custParamRules.filter(r => ruleGroupId(r) === importPage).map(r => r.key)
    );
    const added = [];
    for (const part of kv.split('&')) {
      if (!part) continue;
      const eq = part.indexOf('=');
      const key = (eq >= 0 ? part.slice(0, eq) : part).trim();
      let val = eq >= 0 ? part.slice(eq + 1).trim() : '';
      try { val = decodeURIComponent(val.replace(/\+/g, ' ')); } catch {}
      if (!key || existingKeys.has(key)) continue;
      existingKeys.add(key);
      const isMulti = val.includes(',');
      added.push({
        key, label: '', required: true, severity: 'fail',
        valMode: val ? 'enum' : 'any',
        values: val, pattern: '', pages, multi: isMulti,
      });
    }
    if (!added.length) { setImportMsg('Nothing new to import (all keys already exist)'); return; }
    setDraft(d => ({ ...d, custParamRules: [...d.custParamRules, ...added] }));
    setImportText('');
    setImportMsg(`Added ${added.length} rule${added.length > 1 ? 's' : ''} — review values, then Save`);
    setTimeout(() => setImportMsg(''), 4000);
  }

  // Load a full ruleset JSON (array of serialized rules, or { custParamRules: [...] }).
  // Converts each saved rule into the editor's draft shape and appends (dedup by key+page).
  function importJson() {
    const raw = importText.trim();
    if (!raw) { setImportMsg('Paste a ruleset JSON array first'); return; }
    let parsed;
    try { parsed = JSON.parse(raw); } catch { setImportMsg('Invalid JSON'); return; }
    const list = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.custParamRules) ? parsed.custParamRules : null);
    if (!list) { setImportMsg('Expected a JSON array of rules'); return; }
    const existing = new Set(draft.custParamRules.map(r => `${r.key}|${r.pages || ''}`));
    const added = [];
    for (const r of list) {
      if (!r || !r.key) continue;
      const pages = Array.isArray(r.pages) ? r.pages.join(', ') : (r.pages || '');
      if (existing.has(`${r.key}|${pages}`)) continue;
      existing.add(`${r.key}|${pages}`);
      added.push({
        key: r.key,
        label: r.label || '',
        required: r.required !== false,
        severity: r.severity || 'fail',
        valMode: (r.values && r.values.length) ? 'enum' : (r.pattern ? 'regex' : 'any'),
        values: Array.isArray(r.values) ? r.values.join(', ') : (r.values || ''),
        pattern: r.pattern || '',
        pages,
        multi: !!r.multi,
      });
    }
    if (!added.length) { setImportMsg('Nothing new to import (rules already present)'); return; }
    setDraft(d => ({ ...d, custParamRules: [...d.custParamRules, ...added] }));
    setImportText('');
    setImportMsg(`Loaded ${added.length} rule${added.length > 1 ? 's' : ''} — review, then Save`);
    setTimeout(() => setImportMsg(''), 4000);
  }

  // Plain-English summary of a rule, for the collapsed row.
  const ruleSummary = (rule) => {
    const need = rule.required !== false ? 'required' : 'optional';
    let v;
    if (rule.valMode === 'enum') {
      const vals = (rule.values || '').split(',').map(s => s.trim()).filter(Boolean);
      v = vals.length ? `must be ${rule.multi ? 'all of' : 'one of'} ${vals.join(', ')}` : 'must be one of a list';
    } else if (rule.valMode === 'regex') {
      v = rule.pattern ? `must match /${rule.pattern}/` : 'must match a pattern';
    } else {
      v = 'just needs to be present';
    }
    return { need, v };
  };

  // One rule = one collapsed row (plain English); click to expand the editor.
  const RuleRow = ({ rule, idx }) => {
    const isOpen = expandedRule === idx;
    const { need, v } = ruleSummary(rule);
    const sev = rule.severity || 'fail';
    const pill = sev === 'fail'
      ? { background: 'var(--error-bg)', color: '#A32D2D' }
      : { background: 'var(--warn-bg)', color: '#854F0B' };
    const pageTokens = (rule.pages || '').split(',').map(s => s.trim()).filter(Boolean);
    const multiPage = pageTokens.length > 1;

    return (
      <div style={{ background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--primary)' : 'var(--line)'}`, borderRadius: 7, marginBottom: 6, overflow: 'hidden' }}>
        {/* Collapsed header */}
        <div onClick={() => setExpandedRule(isOpen ? null : idx)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px', cursor: 'pointer' }}>
          <span style={{ color: 'var(--muted)', fontSize: 12, width: 10, flex: '0 0 auto' }}>{isOpen ? '▾' : '▸'}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)', flex: '0 0 auto', minWidth: 58 }}>{rule.key || '(new rule)'}</span>
          <span style={{ flex: 1, fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{need}</b> — {v}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, flex: '0 0 auto', ...pill }}>{sev === 'fail' ? 'Fail' : 'Warn'}</span>
          <button onClick={e => { e.stopPropagation(); removeRule(idx); }} title="Delete rule"
            style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid var(--line)', background: 'transparent', color: '#c62828', cursor: 'pointer', fontSize: 11, flex: '0 0 auto' }}>✕</button>
        </div>

        {/* Expanded editor — reads as a sentence */}
        {isOpen && (
          <div style={{ borderTop: '1px solid var(--line)', background: 'var(--bg)', padding: '10px 12px' }}>
            {/* Identity + scope */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--muted)', lineHeight: 2.2 }}>
              Key
              <input value={rule.key} onChange={e => updateRule(idx, { key: e.target.value })} placeholder="e.g. pageType" style={inp({ width: 130 })} />
              <input value={rule.label} onChange={e => updateRule(idx, { label: e.target.value })} placeholder="label (optional)" style={inp({ width: 150, fontFamily: 'inherit' })} />
              is
              <select value={rule.required !== false ? 'req' : 'opt'} onChange={e => updateRule(idx, { required: e.target.value === 'req' })} style={sel()}>
                <option value="req">required</option>
                <option value="opt">optional</option>
              </select>
              on
              {multiPage ? (
                <input value={rule.pages} onChange={e => updateRule(idx, { pages: e.target.value })} placeholder="event, story" title="Multiple pages (comma-separated)" style={inp({ width: 160 })} />
              ) : (
                <select value={ruleGroupId(rule)} onChange={e => updateRule(idx, { pages: e.target.value === 'all' ? '' : e.target.value })} style={sel()}>
                  {pageTypes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>

            {/* Value check */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--muted)', lineHeight: 2.2, marginTop: 4 }}>
              Value
              <select value={rule.valMode} onChange={e => updateRule(idx, { valMode: e.target.value })} style={sel()}>
                <option value="any">just needs to be present</option>
                <option value="enum">must be one of a list</option>
                <option value="regex">must match a pattern</option>
              </select>
              {rule.valMode === 'enum' && (
                <input value={rule.values} onChange={e => updateRule(idx, { values: e.target.value })} placeholder="s, m, l" style={inp({ flex: '1 1 200px' })} />
              )}
              {rule.valMode === 'regex' && (
                <input value={rule.pattern} onChange={e => updateRule(idx, { pattern: e.target.value })} placeholder="^\d+$" style={inp({ flex: '1 1 200px' })} />
              )}
            </div>

            {/* Advanced — severity + multi-value tucked away */}
            <button onClick={() => setAdvOpen(a => ({ ...a, [idx]: !a[idx] }))}
              style={{ marginTop: 8, fontSize: 11, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
              {advOpen[idx] ? '▾' : '▸'} Advanced
            </button>
            {advOpen[idx] && (
              <div style={{ marginTop: 6, padding: '8px 10px', border: '1px dashed var(--line)', borderRadius: 6, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--muted)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  On failure →
                  <select value={sev} onChange={e => updateRule(idx, { severity: e.target.value })} style={sel()}>
                    <option value="fail">Fail (blocks sign-off)</option>
                    <option value="warn">Warn (flag only)</option>
                  </select>
                </span>
                {(rule.valMode === 'enum' || rule.valMode === 'regex') && (
                  <label title="Value is a comma-separated list — every token must pass"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: 'var(--ink)' }}>
                    <input type="checkbox" checked={!!rule.multi} onChange={e => updateRule(idx, { multi: e.target.checked })} />
                    comma-separated list — every value must pass
                  </label>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--primary)', borderRadius: 10, padding: '16px', marginBottom: 10, marginTop: -8 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: 'var(--ink)' }}>✏ Edit — {prof.name}</div>

      {/* ── Fill gaps from the current session ───────────────────────────────
          Replays this session's captured requests and fills only the empty /
          missing fields (publisher, bundle, Prebid IDs, slots, sizes). */}
      <div style={{ border: '1px dashed var(--primary)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, background: 'var(--primary)0a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={fillGaps} disabled={!sessionRows.length}
            style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none',
                     background: sessionRows.length ? 'var(--primary)' : 'var(--line)', color: '#fff',
                     cursor: sessionRows.length ? 'pointer' : 'not-allowed', opacity: sessionRows.length ? 1 : 0.6 }}>
            ⤵ Fill gaps from current session
          </button>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            {sessionRows.length
              ? `Scans ${sessionRows.length} captured request${sessionRows.length !== 1 ? 's' : ''} and fills only what's missing — won't overwrite existing values.`
              : 'Capture traffic on the Dashboard first, then come back to fill the gaps.'}
          </span>
          {fillMsg && <span style={{ fontSize: 11, color: 'var(--ink)', fontWeight: 600, flexBasis: '100%' }}>✓ {fillMsg}</span>}
        </div>
      </div>

      {/* Identity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        {[['publisherId','Publisher ID'],['bundleId','Bundle ID']].map(([k,l]) => (
          <div key={k}>
            <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 3 }}>{l}</label>
            <input value={draft[k]} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} style={inp({ width: '100%' })} />
          </div>
        ))}
      </div>

      {/* Banner sizes */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 3 }}>Allowed banner sizes (comma-separated)</label>
        <input value={draft.allowedBannerSizes} onChange={e => setDraft(d => ({ ...d, allowedBannerSizes: e.target.value }))} placeholder="300x250, 320x50, 430x67, …" style={inp({ width: '100%' })} />
      </div>

      {/* cust_params Rules — grouped by page type */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
            cust_params Rules
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, color: 'var(--muted)' }}>GAM + IMA requests</span>
            <button onClick={() => setShowImport(s => !s)}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--line)', background: showImport ? 'var(--primary)' : 'transparent', color: showImport ? '#fff' : 'var(--ink)', cursor: 'pointer' }}>
              ⇪ Import from KVP
            </button>
          </div>
        </div>

        {/* Import panel — paste a captured cust_params string, or a full ruleset JSON */}
        {showImport && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--primary)', borderRadius: 7, padding: '10px 12px', marginBottom: 10 }}>
            {/* Mode switch */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {[['kvp', 'From captured KVP'], ['json', 'Load ruleset JSON']].map(([m, lbl]) => (
                <button key={m} onClick={() => { setImportMode(m); setImportMsg(''); }}
                  style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--line)', background: importMode === m ? 'var(--primary)' : 'transparent', color: importMode === m ? '#fff' : 'var(--ink)', cursor: 'pointer', fontWeight: 600 }}>
                  {lbl}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink)', marginBottom: 6, fontWeight: 600 }}>
              {importMode === 'kvp'
                ? 'Paste the cust_params from a captured request — each key becomes a rule.'
                : 'Paste a ruleset JSON array (e.g. a starter ruleset) — all rules load grouped by page.'}
            </div>
            <textarea value={importText} onChange={e => setImportText(e.target.value)}
              placeholder={importMode === 'kvp'
                ? 'league=soccer/wc/league/12&pageType=event&teamHome=soccer/all/teams/755&event=soccer/wc/events/647616…'
                : '[ { "key": "pageType", "values": ["event"], "pages": ["event"], "required": true, "severity": "fail" }, … ]'}
              style={{ ...inp({ width: '100%', fontFamily: 'monospace', fontSize: 11 }), minHeight: 64, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              {importMode === 'kvp' && <>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>Scope to page:</span>
                <select value={importPage} onChange={e => setImportPage(e.target.value)} style={sel({ flex: '0 0 auto' })}>
                  {pageTypes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </>}
              <button onClick={importMode === 'kvp' ? importKvp : importJson}
                style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                {importMode === 'kvp' ? 'Import keys' : 'Load ruleset'}
              </button>
              {importMsg && <span style={{ fontSize: 11, color: 'var(--muted)' }}>{importMsg}</span>}
            </div>
          </div>
        )}

        {draft.custParamRules.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0', fontStyle: 'italic' }}>
            No rules yet — add one below, or import from a captured KVP string. Rules validate required keys (and optionally their values) in cust_params for every GAM and IMA request.
          </div>
        )}

        {/* Grouped rule list */}
        {(() => {
          const groups = {};
          draft.custParamRules.forEach((rule, idx) => {
            const gid = ruleGroupId(rule);
            (groups[gid] = groups[gid] || []).push({ rule, idx });
          });
          const known = pageTypes.map(p => p.id).filter(id => groups[id]);
          const extras = Object.keys(groups).filter(id => !pageTypes.some(p => p.id === id));
          const order = [...known, ...extras];
          return order.map(gid => {
            const title = prettyPageName(gid);
            return (
              <div key={gid} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', padding: '4px 0', borderBottom: '1px solid var(--line)', marginBottom: 6 }}>
                  {title} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· {groups[gid].length} rule{groups[gid].length > 1 ? 's' : ''}</span>
                </div>
                {groups[gid].map(({ rule, idx }) => (
                  <RuleRow key={idx} rule={rule} idx={idx} />
                ))}
              </div>
            );
          });
        })()}

        {/* Add a blank rule — opens inline for editing */}
        <button onClick={addRule}
          style={{ marginTop: 4, padding: '7px 14px', borderRadius: 7, border: '1px dashed var(--line)', background: 'var(--card)', color: 'var(--primary)', fontSize: 12, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
          + Add rule
        </button>
      </div>

      {/* Prebid mappings */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Prebid Stored Request IDs</div>
        {Object.entries(draft.prebidMappings).map(([key, id]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
            <span style={{ flex: 2, fontFamily: 'monospace', fontSize: 11, color: 'var(--ink)' }}>{key}</span>
            <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 11, color: 'var(--primary)', fontWeight: 700 }}>{id}</span>
            <button onClick={() => { if (!confirmDelete(`the "${key}" Prebid mapping`, { detail: 'Removed when you Save. Click Cancel to keep it.' })) return; setDraft(d => { const m = { ...d.prebidMappings }; delete m[key]; return { ...d, prebidMappings: m }; }); }}
              style={{ fontSize: 10, padding: '1px 7px', borderRadius: 4, border: '1px solid var(--line)', background: 'transparent', color: '#c62828', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input value={newPrebidKey} onChange={e => setNewPrebidKey(e.target.value)} placeholder="fnc_iosmob_hp_banner" style={inp({ flex: 2 })} />
          <input value={newPrebidId}  onChange={e => setNewPrebidId(e.target.value)}  placeholder="Stored request ID" style={inp({ flex: 1 })} />
          <button onClick={addPrebid} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 12, cursor: 'pointer' }}>+ Add</button>
        </div>
      </div>

      {/* Amazon slots */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Amazon APS Slots</div>
        {Object.entries(draft.amazonSlotIds).map(([uuid, d]) => (
          <div key={uuid} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 11 }}>
            <span style={{ fontFamily: 'monospace', color: 'var(--muted)', flex: '0 0 120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{uuid.slice(0,14)}…</span>
            <span style={{ flex: 1, color: 'var(--ink)' }}>{d.name}</span>
            <span style={{ color: 'var(--muted)' }}>{(d.sizes || []).join(', ')}</span>
            <button onClick={() => { if (!confirmDelete(`the "${d.name || uuid.slice(0,14)}" Amazon slot`, { detail: 'Removed when you Save. Click Cancel to keep it.' })) return; setDraft(d2 => { const s = { ...d2.amazonSlotIds }; delete s[uuid]; return { ...d2, amazonSlotIds: s }; }); }}
              style={{ fontSize: 10, padding: '1px 7px', borderRadius: 4, border: '1px solid var(--line)', background: 'transparent', color: '#c62828', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <input value={newSlotUuid}  onChange={e => setNewSlotUuid(e.target.value)}  placeholder="Slot UUID (from amznslots)" style={inp({ flex: '2 1 180px' })} />
          <input value={newSlotName}  onChange={e => setNewSlotName(e.target.value)}  placeholder="Label (e.g. HP Banner)" style={inp({ flex: '1 1 120px' })} />
          <input value={newSlotSizes} onChange={e => setNewSlotSizes(e.target.value)} placeholder="320x50, 300x250" style={inp({ flex: '1 1 100px' })} />
          <button onClick={addSlot} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>+ Add</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>Cancel</button>
        <button onClick={save} style={{ fontSize: 12, padding: '6px 18px', borderRadius: 7, border: 'none', background: saved ? '#1a6f3c' : 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
          {saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

/* ── Wizard shared components ─────────────────────────────────────────────── */
function WizardOverlay({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', zIndex: 9000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--card)', borderRadius: 12, padding: 24,
        maxWidth: 480, width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--ink)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)', lineHeight: 1 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function WizardNav({ onBack, onNext, nextLabel = 'Next →', nextDisabled = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <button onClick={onBack} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>
        ← Back
      </button>
      {onNext && (
        <button onClick={onNext} disabled={nextDisabled}
          style={{ fontSize: 12, padding: '6px 18px', borderRadius: 7, border: 'none', background: nextDisabled ? 'var(--muted)' : 'var(--primary)', color: '#fff', fontWeight: 700, cursor: nextDisabled ? 'default' : 'pointer' }}>
          {nextLabel}
        </button>
      )}
    </div>
  );
}

// ─── SSL Proxying — Exclude List ──────────────────────────────────────────────
// Per-app SSL exclusion list — stored in the app profile (localStorage).
// Optionally syncs to the proxy server's live exclude list via the API.

function SslProxyingSection() {
  const { appId } = getActiveSelection();
  const apps = getAllApps();
  const appName = apps[appId]?.name || appId;

  const [list, setList]         = useState(() => getAppSslExclusions(appId));
  const [input, setInput]       = useState('');
  const [inputErr, setInputErr] = useState('');
  const [syncMsg, setSyncMsg]   = useState('');
  const inputRef = useRef(null);

  // Reload when active app changes
  useEffect(() => {
    setList(getAppSslExclusions(appId));
  }, [appId]);

  function persist(next) {
    setAppSslExclusions(appId, next);
    setList(next);
  }

  function add() {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed) return;
    if (!/^[\w.*?-]+(\.[\w.*?-]+)*$/.test(trimmed)) {
      setInputErr('Use a hostname, *.example.com, or api?.example.com');
      return;
    }
    if (list.includes(trimmed)) { setInputErr('Already in list'); return; }
    setInputErr('');
    persist([...list, trimmed]);
    setInput('');
    inputRef.current?.focus();
  }

  function remove(pattern) {
    if (!confirmDelete(`the SSL exclusion "${pattern}"`)) return;
    persist(list.filter(p => p !== pattern));
  }

  async function syncToProxy() {
    const proxyApiUrl = (() => {
      try {
        // Try env var first (baked in at build), fall back to localStorage override
        const envUrl = process.env.REACT_APP_PROXY_API_URL;
        const stored = localStorage.getItem('proxy_api_url');
        return ((envUrl || stored) || '').replace(/\/$/, '');
      } catch { return ''; }
    })();
    if (!proxyApiUrl) {
      setSyncMsg('⚠️ Proxy API URL not set — configure it in Proxy Settings');
      return;
    }
    try {
      setSyncMsg('Syncing…');
      const r = await fetch(`${proxyApiUrl}/proxy/ssl-exclude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patterns: list }),
      });
      if (!r.ok) throw new Error(r.status);
      setSyncMsg('✅ Synced to proxy server');
    } catch {
      setSyncMsg('❌ Could not reach proxy server');
    }
    setTimeout(() => setSyncMsg(''), 3000);
  }

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader
        title="🔒 SSL Proxying — Exclude List"
        badge={`${list.length} pattern${list.length !== 1 ? 's' : ''}`}
      >
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {appName} profile
        </span>
      </SectionHeader>
      <div style={SECTION_BODY}>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
          Domains in this list bypass SSL decryption for <strong>{appName}</strong> — the proxy
          tunnels them as raw TCP. Use for certificate-pinned domains that break when intercepted.
          Supports <code>*</code> and <code>?</code> wildcards.
        </p>

        {/* Add row */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => { setInput(e.target.value); setInputErr(''); }}
            onKeyDown={e => e.key === 'Enter' && add()}
            placeholder="e.g. *.crashlytics.com or firebaselogging-pa.googleapis.com"
            style={{
              flex: 1, padding: '7px 10px', fontSize: 13, fontFamily: 'monospace',
              border: `1px solid ${inputErr ? '#c62828' : 'var(--line)'}`,
              borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)',
            }}
          />
          <button onClick={add} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
            Add
          </button>
        </div>
        {inputErr && <div style={{ fontSize: 12, color: '#c62828', marginBottom: 8 }}>{inputErr}</div>}

        {/* Pattern list */}
        {list.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            No exclusions — all domains are SSL-proxied for {appName}.
          </div>
        ) : (
          <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginTop: 8 }}>
            {list.map((pattern, idx) => (
              <div key={pattern} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: idx === 0 ? 'none' : '1px solid var(--line)', background: idx % 2 === 0 ? 'var(--card)' : 'var(--bg)' }}>
                <code style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'monospace' }}>{pattern}</code>
                <button onClick={() => remove(pattern)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Sync to proxy */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button onClick={syncToProxy} style={{ fontSize: 12, padding: '5px 14px', borderRadius: 6, border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer' }}>
            ↑ Push to proxy server
          </button>
          {syncMsg && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{syncMsg}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── IVT Checks — global feature switch ─────────────────────────────────────────
// Single on/off toggle for the IVT (invalid-traffic) check family: nbr IVT
// escalation, gam_ms_missing, the qaRunner fill-collapse triage finding, the
// verification-never-fired session check, and the datacenter-egress
// self-check (proxy-server/ivtConfig.js). GET/POST /proxy/ivt-config.
function IvtChecksSection() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [msg, setMsg] = useState('');
  const proxyApiUrl = (() => {
    try {
      const envUrl = process.env.REACT_APP_PROXY_API_URL;
      const stored = localStorage.getItem('proxy_api_url');
      return ((envUrl || stored) || '').replace(/\/$/, '');
    } catch { return ''; }
  })();

  useEffect(() => {
    if (!proxyApiUrl) { setMsg('⚠️ Proxy API URL not set — configure it in Proxy Settings'); return; }
    fetch(`${proxyApiUrl}/proxy/ivt-config`, { signal: AbortSignal.timeout(4000) })
      .then(r => r.json()).then(j => setEnabled(!!j.enabled))
      .catch(() => setMsg('❌ Could not reach proxy server'));
  }, [proxyApiUrl]);

  async function toggle(next) {
    if (!proxyApiUrl) return;
    setEnabled(next); // optimistic — flip back on failure
    try {
      setMsg('Saving…');
      const r = await fetch(`${proxyApiUrl}/proxy/ivt-config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) throw new Error(r.status);
      const j = await r.json();
      setEnabled(!!j.enabled);
      setMsg('✅ Saved — applies live (no restart)');
    } catch {
      setEnabled(!next);
      setMsg('❌ Save failed');
    }
    setTimeout(() => setMsg(''), 3000);
  }

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title="🛡️ IVT Checks">
        {enabled !== null && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <Switch
              checked={!!enabled}
              onChange={e => toggle(e.target.checked)}
              size="small"
            />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{enabled ? 'on' : 'off'}</span>
          </label>
        )}
      </SectionHeader>
      <div style={SECTION_BODY}>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
          IVT detection (nbr verdicts, anti-spam signal, fill-collapse, egress check) — a single
          switch for the invalid-traffic check family: OpenRTB <code>nbr</code> IVT escalation,
          missing GAM measurement-session signals, the qaRunner fill-collapse triage finding, the
          verification-never-fired session check, and the proxy's own datacenter-egress
          self-check. Saved here and applied live — no restart.
        </p>
        {enabled === null ? (
          <div style={{ padding: '8px 0', color: 'var(--muted)', fontSize: 13 }}>{msg || 'Loading…'}</div>
        ) : (
          msg && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{msg}</div>
        )}
      </div>
    </div>
  );
}

// ─── Video health monitor thresholds ────────────────────────────────────────────
function VideoMonitorSection() {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState('');
  const proxyApiUrl = (() => {
    try {
      const envUrl = process.env.REACT_APP_PROXY_API_URL;
      const stored = localStorage.getItem('proxy_api_url');
      return ((envUrl || stored) || '').replace(/\/$/, '');
    } catch { return ''; }
  })();

  useEffect(() => {
    if (!proxyApiUrl) { setMsg('⚠️ Proxy API URL not set — configure it in Proxy Settings'); return; }
    fetch(`${proxyApiUrl}/proxy/monitor-config`, { signal: AbortSignal.timeout(4000) })
      .then(r => r.json()).then(j => setCfg(j.config))
      .catch(() => setMsg('❌ Could not reach proxy server'));
  }, [proxyApiUrl]);

  async function save() {
    if (!proxyApiUrl || !cfg) return;
    try {
      setMsg('Saving…');
      const r = await fetch(`${proxyApiUrl}/proxy/monitor-config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg),
      });
      if (!r.ok) throw new Error(r.status);
      const j = await r.json();
      setCfg(j.config);
      setMsg('✅ Saved — the monitor applies it live (no restart)');
    } catch { setMsg('❌ Save failed'); }
    setTimeout(() => setMsg(''), 3500);
  }

  const FIELDS = [
    { key: 'minFill',       label: 'Min fill %',           hint: 'Alert if fewer ads serve per opportunity' },
    { key: 'minCompletion', label: 'Min completion %',     hint: 'Alert if fewer ads play to 100%' },
    { key: 'maxNoBid',      label: 'Max SS no-fill %',     hint: 'Alert if SpringServe no-bid rate spikes' },
    { key: 'maxErrorRate',  label: 'Max VAST errors /100', hint: 'Alert on VAST error spikes' },
    { key: 'maxHlsErrors',  label: 'Max HLS failures',     hint: 'Alert if N+ HLS manifest/segment requests return 4xx/5xx' },
    { key: 'minSample',     label: 'Min sample',           hint: 'Min ad opportunities before alerting' },
    { key: 'windowSec',     label: 'Window (sec)',         hint: 'Rolling window length' },
    { key: 'intervalSec',   label: 'Interval (sec)',       hint: 'Evaluate every N seconds' },
  ];

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title="📊 Video Health Monitor — Alert Thresholds">
        {cfg && (
          <label style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={cfg.enabled !== false} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
            enabled
          </label>
        )}
      </SectionHeader>
      <div style={SECTION_BODY}>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
          Thresholds for the live video health monitor (<code>scripts/video-health-monitor.js</code>).
          It taps the proxy, tracks rolling video metrics, and alerts (console + webhook) when these
          breach. Saved here and applied live — no restart.
        </p>

        {!cfg ? (
          <div style={{ padding: '16px 0', color: 'var(--muted)', fontSize: 13 }}>{msg || 'Loading…'}</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {FIELDS.map(({ key, label, hint }) => (
                <div key={key}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 3 }} title={hint}>{label}</label>
                  <input
                    type="number" min="0" value={cfg[key]}
                    onChange={e => setCfg({ ...cfg, [key]: e.target.value === '' ? '' : Number(e.target.value) })}
                    style={{ width: '100%', padding: '6px 9px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)' }}
                  />
                  <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>{hint}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 3 }}>Alert webhook (Slack / Discord / generic JSON)</label>
              <input
                value={cfg.webhook || ''} placeholder="https://hooks.slack.com/services/…"
                onChange={e => setCfg({ ...cfg, webhook: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', fontSize: 13, fontFamily: 'monospace', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button onClick={save} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Save thresholds
              </button>
              {msg && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{msg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Automation: device driver + qa-run gate ────────────────────────────────────
// Everything the headless gate (scripts/qa-run.js) and the Maestro device driver
// (deviceDriver/) need, edited here and persisted to data/device-config.json — no
// hand-edited JSON. Saved live; the next gate run picks it up.

const INPUT_STYLE = {
  width: '100%', padding: '6px 9px', fontSize: 13, border: '1px solid var(--line)',
  borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)',
};
const FIELD_LABEL = { fontSize: 12, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 3 };

const GATE_FIELDS = [
  { key: 'durationSec',      label: 'Capture window (sec)', hint: 'Max seconds to capture per gate run' },
  { key: 'idleSec',          label: 'Idle stop (sec)',      hint: 'Stop early after this much traffic silence' },
  { key: 'cpmDropTolerance', label: 'CPM drop tolerance',   hint: '0–1; CPM drop beyond this vs baseline → WARN', step: '0.05' },
];
const SELECTOR_FIELDS = [
  { key: 'homeTab',         label: 'Home tab',         hint: 'Text/id to open the home feed' },
  { key: 'forYouTab',       label: 'For You tab',      hint: 'Text/id to open the For You feed' },
  { key: 'videoTab',        label: 'Video / Watch tab',hint: 'Text/id to open a video' },
  { key: 'articleHeadline', label: 'Article headline', hint: 'Text/id to open an article (blank = scroll only)' },
  { key: 'anyAd',           label: 'Ad (click test)',  hint: 'Selector for an ad to tap; blank skips the click test' },
];
const TIMING_FIELDS = [
  { key: 'scrollsPerFeedStep',   label: 'Scrolls / feed step', hint: 'How many scrolls per feed page' },
  { key: 'dwellMsBetweenScrolls',label: 'Dwell (ms)',          hint: 'Pause between scrolls so ads load' },
  { key: 'videoDwellMs',         label: 'Video dwell (ms)',    hint: 'How long to let a video play' },
];

function AutomationSection() {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState('');
  const proxyApiUrl = (() => {
    try {
      const envUrl = process.env.REACT_APP_PROXY_API_URL;
      const stored = localStorage.getItem('proxy_api_url');
      return ((envUrl || stored) || '').replace(/\/$/, '');
    } catch { return ''; }
  })();

  useEffect(() => {
    if (!proxyApiUrl) { setMsg('⚠️ Proxy API URL not set — configure it in Proxy Settings'); return; }
    fetch(`${proxyApiUrl}/proxy/device-config`, { signal: AbortSignal.timeout(4000) })
      .then(r => r.json()).then(j => setCfg(j.config))
      .catch(() => setMsg('❌ Could not reach proxy server'));
  }, [proxyApiUrl]);

  function setGate(key, val) { setCfg(c => ({ ...c, gate: { ...c.gate, [key]: val } })); }
  function setApp(app, path, val) {
    setCfg(c => {
      const next = JSON.parse(JSON.stringify(c));
      let o = next.apps[app];
      const parts = path.split('.');
      for (let i = 0; i < parts.length - 1; i++) o = o[parts[i]] = o[parts[i]] || {};
      o[parts[parts.length - 1]] = val;
      return next;
    });
  }

  async function save() {
    if (!proxyApiUrl || !cfg) return;
    try {
      setMsg('Saving…');
      const r = await fetch(`${proxyApiUrl}/proxy/device-config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg),
      });
      if (!r.ok) throw new Error(r.status);
      const j = await r.json();
      setCfg(j.config);
      setMsg('✅ Saved — the next gate run uses these settings');
    } catch { setMsg('❌ Save failed'); }
    setTimeout(() => setMsg(''), 3500);
  }

  async function regenerate() {
    if (!proxyApiUrl) return;
    try {
      setMsg('Saving + regenerating flows…');
      await fetch(`${proxyApiUrl}/proxy/device-config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg),
      });
      const r = await fetch(`${proxyApiUrl}/proxy/device-config/generate-flows`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || r.status);
      setMsg(`✅ Generated ${j.written.length} flow(s)${j.warnings && j.warnings.length ? ` · ${j.warnings.length} warning(s)` : ''}`);
    } catch { setMsg('❌ Flow generation failed'); }
    setTimeout(() => setMsg(''), 4000);
  }

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title="🤖 Automation — Device Driver & Gate" badge={cfg ? `${Object.keys(cfg.apps || {}).length} app(s)` : ''} />
      <div style={SECTION_BODY}>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 14 }}>
          Settings for the headless build gate (<code>qa-run</code>) and the Maestro device driver.
          Edited here and saved to the proxy — the next <code>qa-gate-device</code> run regenerates its
          flow from these values. Set the app launch IDs and (optionally) tap selectors per platform.
        </p>

        {!cfg ? (
          <div style={{ padding: '16px 0', color: 'var(--muted)', fontSize: 13 }}>{msg || 'Loading…'}</div>
        ) : (
          <>
            {/* Gate */}
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Gate</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {GATE_FIELDS.map(({ key, label, hint, step }) => (
                <div key={key}>
                  <label style={FIELD_LABEL} title={hint}>{label}</label>
                  <input type="number" min="0" step={step || '1'} value={cfg.gate[key]}
                    onChange={e => setGate(key, e.target.value === '' ? '' : Number(e.target.value))}
                    style={INPUT_STYLE} />
                  <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>{hint}</div>
                </div>
              ))}
              <div>
                <label style={FIELD_LABEL} title="How to treat a slot that appears but wasn't in the baseline">New slot</label>
                <select value={cfg.gate.treatNewSlotAs} onChange={e => setGate('treatNewSlotAs', e.target.value)} style={INPUT_STYLE}>
                  <option value="info">info (don't fail)</option>
                  <option value="warn">warn</option>
                </select>
                <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>Unexpected new slot handling</div>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={FIELD_LABEL}>Regression webhook (blank = use Video Monitor webhook)</label>
              <input value={cfg.gate.webhook || ''} placeholder="https://hooks.slack.com/services/…"
                onChange={e => setGate('webhook', e.target.value)}
                style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} />
            </div>

            {/* Per-app */}
            {Object.entries(cfg.apps).map(([app, a]) => (
              <div key={app} style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                  {a.label || app} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· {app}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={FIELD_LABEL} title="iOS bundle identifier, e.g. com.example.app">iOS appId (bundle id)</label>
                    <input value={a.platforms?.ios?.appId || ''} placeholder="com.example.app"
                      onChange={e => setApp(app, 'platforms.ios.appId', e.target.value)} style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} />
                  </div>
                  <div>
                    <label style={FIELD_LABEL} title="Android package name, e.g. com.example.app">Android appId (package)</label>
                    <input value={a.platforms?.android?.appId || ''} placeholder="com.example.app"
                      onChange={e => setApp(app, 'platforms.android.appId', e.target.value)} style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} />
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', margin: '12px 0 6px' }}>Selectors (blank = scroll-only)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {SELECTOR_FIELDS.map(({ key, label, hint }) => (
                    <div key={key}>
                      <label style={FIELD_LABEL} title={hint}>{label}</label>
                      <input value={a.selectors?.[key] || ''} onChange={e => setApp(app, `selectors.${key}`, e.target.value)} style={INPUT_STYLE} />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', margin: '12px 0 6px' }}>Timing</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {TIMING_FIELDS.map(({ key, label, hint }) => (
                    <div key={key}>
                      <label style={FIELD_LABEL} title={hint}>{label}</label>
                      <input type="number" min="0" value={a.timing?.[key]} onChange={e => setApp(app, `timing.${key}`, e.target.value === '' ? '' : Number(e.target.value))} style={INPUT_STYLE} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
              <button onClick={save} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Save
              </button>
              <button onClick={regenerate} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Save + regenerate flows
              </button>
              {msg && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{msg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Scan Playlists editor ───────────────────────────────────────────────────────
// The per-app QA checklists that drive the Scan page AND the Maestro flow generation.
// Edited here and persisted to the proxy (data/playlists.json) — no code edits to add an
// app or a step.

const DETECT_FLAGS = [
  { key: 'impression',  label: 'Impression' },
  { key: 'viewability', label: 'Viewability' },
  { key: 'refresh',     label: 'Refresh' },
  { key: 'ima',         label: 'IMA (video)' },
];

function blankStep(n) {
  return {
    id: `step${n}`, label: `Step ${n}`, instruction: '', pageType: '',
    detect: { pageType: '', minSlots: 1, wantSlots: [], impression: false, viewability: false, refresh: false, ima: false },
    clickTest: { enabled: false, instruction: '', timeoutMs: 15000 }, timeoutMs: 30000,
  };
}

function ScanPlaylistsSection() {
  const [pl, setPl] = useState(null);   // { appId: {id,label,steps:[...]} }
  const [msg, setMsg] = useState('');
  const proxyApiUrl = (() => {
    try {
      const envUrl = process.env.REACT_APP_PROXY_API_URL;
      const stored = localStorage.getItem('proxy_api_url');
      return ((envUrl || stored) || '').replace(/\/$/, '');
    } catch { return ''; }
  })();

  useEffect(() => {
    if (!proxyApiUrl) { setMsg('⚠️ Proxy API URL not set — configure it in Proxy Settings'); return; }
    fetch(`${proxyApiUrl}/proxy/playlists`, { signal: AbortSignal.timeout(4000) })
      .then(r => r.json()).then(j => setPl(j.playlists || {}))
      .catch(() => setMsg('❌ Could not reach proxy server'));
  }, [proxyApiUrl]);

  const clone = () => JSON.parse(JSON.stringify(pl));
  function setApp(appId, field, val) { const n = clone(); n[appId][field] = val; setPl(n); }
  function setStep(appId, i, path, val) {
    const n = clone(); let o = n[appId].steps[i];
    const parts = path.split('.');
    for (let k = 0; k < parts.length - 1; k++) o = o[parts[k]] = o[parts[k]] || {};
    o[parts[parts.length - 1]] = val; setPl(n);
  }
  function addStep(appId) { const n = clone(); n[appId].steps.push(blankStep(n[appId].steps.length + 1)); setPl(n); }
  function removeStep(appId, i) { const n = clone(); n[appId].steps.splice(i, 1); setPl(n); }
  function addApp() {
    const id = (prompt('New app id (e.g. fbn, fwx)?') || '').trim();
    if (!id) return;
    const n = clone(); if (n[id]) { setMsg('App id already exists'); return; }
    n[id] = { id, label: id, steps: [blankStep(1)] }; setPl(n);
  }
  function removeApp(appId) { if (!window.confirm(`Remove playlist "${appId}"?`)) return; const n = clone(); delete n[appId]; setPl(n); }

  async function save() {
    if (!proxyApiUrl || !pl) return;
    try {
      setMsg('Saving…');
      const r = await fetch(`${proxyApiUrl}/proxy/playlists`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ playlists: pl }),
      });
      if (!r.ok) throw new Error(r.status);
      const j = await r.json();
      setPl(j.playlists);
      setMsg('✅ Saved — drives the Scan page and device flows');
    } catch { setMsg('❌ Save failed'); }
    setTimeout(() => setMsg(''), 3500);
  }

  const wantStr = (ws) => Array.isArray(ws) ? ws.join(', ') : (ws || '');

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title="📋 Scan Playlists" badge={pl ? `${Object.keys(pl).length} app(s)` : ''}>
        {pl && (
          <button onClick={addApp} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ App</button>
        )}
      </SectionHeader>
      <div style={SECTION_BODY}>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 14 }}>
          Per-app QA checklist steps. These drive the guided <strong>Scan</strong> page and are the source
          the Maestro device flows are generated from. Add an app or step here — no code change.
        </p>

        {!pl ? (
          <div style={{ padding: '16px 0', color: 'var(--muted)', fontSize: 13 }}>{msg || 'Loading…'}</div>
        ) : (
          <>
            {Object.entries(pl).map(([appId, app]) => (
              <div key={appId} style={{ marginBottom: 18, border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--card)', borderBottom: '1px solid var(--line)' }}>
                  <input value={app.label} onChange={e => setApp(appId, 'label', e.target.value)} style={{ ...INPUT_STYLE, fontWeight: 700, maxWidth: 220 }} />
                  <Tag>{appId}</Tag>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{app.steps.length} step(s)</span>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => addStep(appId)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 12, cursor: 'pointer' }}>+ Step</button>
                  <button onClick={() => removeApp(appId)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #c6282833', background: 'var(--bg)', color: '#c62828', fontSize: 12, cursor: 'pointer' }}>Remove app</button>
                </div>
                <div style={{ padding: 12 }}>
                  {app.steps.map((s, i) => (
                    <div key={i} style={{ padding: 10, marginBottom: 10, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 8 }}>
                        <div>
                          <label style={FIELD_LABEL}>Label</label>
                          <input value={s.label} onChange={e => setStep(appId, i, 'label', e.target.value)} style={INPUT_STYLE} />
                        </div>
                        <div>
                          <label style={FIELD_LABEL} title="GAM iu page segment, e.g. hp / art / fyp">Page type</label>
                          <input value={s.pageType} onChange={e => { setStep(appId, i, 'pageType', e.target.value); setStep(appId, i, 'detect.pageType', e.target.value); }} style={INPUT_STYLE} />
                        </div>
                        <div>
                          <label style={FIELD_LABEL}>Min slots</label>
                          <input type="number" min="0" value={s.detect.minSlots} onChange={e => setStep(appId, i, 'detect.minSlots', e.target.value === '' ? '' : Number(e.target.value))} style={INPUT_STYLE} />
                        </div>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <label style={FIELD_LABEL} title="Drives the on-device tester prompt + the Maestro flow navigation">Instruction</label>
                        <input value={s.instruction} onChange={e => setStep(appId, i, 'instruction', e.target.value)} style={INPUT_STYLE} />
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <label style={FIELD_LABEL} title="Ideal slots to see (coverage), comma-separated">Want slots</label>
                        <input value={wantStr(s.detect.wantSlots)} onChange={e => setStep(appId, i, 'detect.wantSlots', e.target.value)} style={INPUT_STYLE} placeholder="adh, ban1, ban2" />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10, alignItems: 'center' }}>
                        {DETECT_FLAGS.map(({ key, label }) => (
                          <label key={key} style={{ fontSize: 12, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!s.detect[key]} onChange={e => setStep(appId, i, `detect.${key}`, e.target.checked)} />
                            {label}
                          </label>
                        ))}
                        <span style={{ width: 1, height: 16, background: 'var(--line)' }} />
                        <label style={{ fontSize: 12, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!s.clickTest.enabled} onChange={e => setStep(appId, i, 'clickTest.enabled', e.target.checked)} />
                          Click test
                        </label>
                        <div style={{ flex: 1 }} />
                        <button onClick={() => removeStep(appId, i)} style={{ padding: '3px 9px', borderRadius: 6, border: '1px solid #c6282833', background: 'var(--bg)', color: '#c62828', fontSize: 11, cursor: 'pointer' }}>Remove</button>
                      </div>
                    </div>
                  ))}
                  {app.steps.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)', padding: 8 }}>No steps — add one.</div>}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <button onClick={save} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Save playlists
              </button>
              {msg && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{msg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One editor for a pack's per-param rule block. Instantiated three times (GAM
 * ad-tag params, APS SDK body fields, Prebid ORTB fields) rather than copied,
 * because the blocks are the same rule shape by design (spec §3.2) — forking
 * the editor would let them drift in exactly the way the shared shape exists to
 * prevent.
 *
 * `field` is the pack key being edited ('gamParamRules' | 'apsParamRules' |
 * 'prebidParamRules'). `showCreate` puts the pack-creation form in this
 * instance; only one section should carry it, or two forms compete to create
 * the same pack.
 *
 * Three props exist for Prebid's deviations, all forced by the wire format:
 * `keyField` — Prebid rules are keyed by `path`, a dot path into the ORTB body,
 * not `key`; `showPlatform` — an ORTB body carries no platform field, so a
 * per-platform rule there would scope to nothing; `extraMatchers` — Prebid
 * rules may match on `range` or on a derived `mappings` map, and the
 * "pattern or values is required" check must not reject those.
 */
function ParamRulesSection({
  field,
  icon,
  title,
  blurb,
  keyHelp,
  keyPlaceholder,
  patternPlaceholder,
  valuesPlaceholder,
  showCreate = false,
  keyField = 'key',
  keyLabel = 'Key',
  showPlatform = true,
  extraMatchers = [],
}) {
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [rules, setRules] = useState([]);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const proxyApiUrl = (() => {
    try {
      const envUrl = process.env.REACT_APP_PROXY_API_URL;
      const stored = localStorage.getItem('proxy_api_url');
      return ((envUrl || stored) || '').replace(/\/$/, '');
    } catch { return ''; }
  })();

  // Re-read on every store change. A [] memo froze this at mount, so a pack
  // created or saved here did not appear until a full reload.
  const [packsVersion, setPacksVersion] = useState(0);
  const packs = useMemo(() => getPacks(), [packsVersion]);
  useEffect(() => subscribeAppPacks(() => setPacksVersion((v) => v + 1)), []);
  const packEntries = useMemo(() => Object.entries(packs || {}).sort((a, b) => (a[0] || '').localeCompare(b[0] || '')), [packs]);

  // When a pack is selected, load its gamParamRules
  useEffect(() => {
    if (!selectedSlug || !packs[selectedSlug]) {
      setSelectedPack(null);
      setRules([]);
      return;
    }
    const pack = packs[selectedSlug];
    setSelectedPack(pack);
    setRules(JSON.parse(JSON.stringify(pack[field] || [])));
    setMsg('');
  }, [selectedSlug, packs, field]);

  /* ── Create a pack ────────────────────────────────────────────────────────
   * Without this the BU layer is unreachable: rules attach to a pack, packs
   * are matched to traffic by networkId + brandCode, and the only packs that
   * ship are the brand-neutral `generic` template and `tmz`. Traffic on any
   * other network resolved to no pack, so the Business-unit column read "—"
   * forever with nowhere in the UI to fix it. No branded pack is committed —
   * the tool stays app-agnostic; yours is created at runtime and persists in
   * the proxy's own store.
   */
  const [newSlug, setNewSlug] = useState('');
  const [newNetworkId, setNewNetworkId] = useState('');
  const [newBrands, setNewBrands] = useState('');
  const [creating, setCreating] = useState(false);

  const createPack = async () => {
    const slug = newSlug.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '');
    const networkId = newNetworkId.trim();
    const brandCodes = newBrands.split(',').map((x) => x.trim()).filter(Boolean);
    if (!slug || !networkId || !brandCodes.length) {
      setMsg('❌ slug, network ID and at least one brand code are all required — the pack is matched on network + brand');
      return;
    }
    if (packs && packs[slug]) { setMsg(`❌ a pack named "${slug}" already exists`); return; }
    try {
      setCreating(true);
      setMsg('Creating…');
      const res = await fetch(`${proxyApiUrl}/proxy/apps/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug, label: slug, enabled: true,
          identity: { networkId, brandCodes },
          [field]: [],
        }),
      });
      if (!res.ok) throw new Error(res.status);
      await refreshAppPacks();
      setSelectedSlug(slug);
      setNewSlug(''); setNewNetworkId(''); setNewBrands('');
      setMsg('✅ Pack created — add rules below');
    } catch (e) {
      setMsg(`❌ Create failed: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Validate a regex pattern
  const validatePattern = (pattern) => {
    try {
      new RegExp(pattern);
      return { valid: true, error: null };
    } catch (e) {
      return { valid: false, error: String(e).replace('Error: ', '') };
    }
  };

  // Update a rule field
  const updateRule = (idx, field, value) => {
    const newRules = [...rules];
    if (field === 'pattern') {
      newRules[idx].pattern = value;
      if (value) { delete newRules[idx].values; delete newRules[idx].range; }
    } else if (field === 'values') {
      newRules[idx].values = value;
      if (value && value.length > 0) { delete newRules[idx].pattern; delete newRules[idx].range; }
    } else if (field === 'range') {
      // A rule carries exactly one matcher; sanitizePack keeps the others but
      // the evaluator reads pattern, then values, then range, so leaving two
      // set would make the editor show one rule and the engine apply another.
      if (value) { newRules[idx].range = value; delete newRules[idx].pattern; delete newRules[idx].values; }
      else delete newRules[idx].range;
    } else if (field === 'derived') {
      // When editing a derived rule, mark it as overridden (drop derived flag)
      if (newRules[idx].derived && newRules[idx].derived !== value) {
        delete newRules[idx].derived;
      }
      newRules[idx][field] = value;
    } else {
      newRules[idx][field] = value;
    }
    setRules(newRules);
  };

  // Remove a rule
  const removeRule = (idx) => {
    setRules(rules.filter((_, i) => i !== idx));
  };

  /* Pre-fill from the pack's own app profile (spec §3.3).
   *
   * The derivations live server-side (validation/apsParamDerive.js,
   * gamParamDerive.js) and are FETCHED, never re-implemented here: a second
   * copy in the browser would drift from the one the Checks run actually
   * evaluates, and the editor would then show rules the engine does not use.
   *
   * Derived rules are additive and never clobber a typed one for the same
   * key+platform — editing a derived rule is what makes it an override, and an
   * override is permanent until the user clears it.
   */
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    if (!proxyApiUrl || !selectedSlug) { setMsg('❌ No pack selected or proxy not available'); return; }
    try {
      setPrefilling(true);
      setMsg('Deriving…');
      const res = await fetch(`${proxyApiUrl}/proxy/apps/${selectedSlug}/derived-rules`);
      if (!res.ok) throw new Error(res.status);
      const j = await res.json();
      const derived = Array.isArray(j[field]) ? j[field] : [];
      if (!derived.length) {
        setMsg('Nothing to derive — this pack has no app profile fields to read (bundle ID, APS slot UUIDs).');
        return;
      }
      const seen = new Set(rules.map((r) => `${r[keyField]}|${r.platform || ''}`));
      const added = derived.filter((r) => r && r[keyField] && !seen.has(`${r[keyField]}|${r.platform || ''}`));
      if (!added.length) {
        setMsg('Nothing to add — every derivable rule is already listed.');
        return;
      }
      setRules([...rules, ...added]);
      setMsg(`Added ${added.length} derived rule(s) — review, then Save.`);
    } catch (e) {
      setMsg(`❌ Pre-fill failed: ${e.message}`);
    } finally {
      setPrefilling(false);
    }
  };

  // Add a new rule
  const addRule = () => {
    setRules([...rules, {
      [keyField]: '',
      label: '',
      required: false,
      severity: 'warn',
      ...(showPlatform ? { platform: null } : {}),
    }]);
  };

  // Save rules to the pack
  const save = async () => {
    if (!proxyApiUrl || !selectedSlug || !selectedPack) {
      setMsg('❌ No pack selected or proxy not available');
      return;
    }

    // Validate all rules
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      const ruleKey = rule[keyField];
      if (!ruleKey || !String(ruleKey).trim()) {
        setMsg(`❌ Rule ${i + 1}: ${keyField} is required`);
        return;
      }
      if (rule.pattern) {
        const { valid, error } = validatePattern(rule.pattern);
        if (!valid) {
          setMsg(`❌ Rule ${i + 1} (${ruleKey}): invalid regex — ${error}`);
          return;
        }
      }
      // A rule with no matcher at all still asserts presence when it is
      // required; one that is neither required nor matching anything is a rule
      // that cannot fail, which is worse than no rule (it reports a pass).
      const hasMatcher = !!rule.pattern || !!rule.values
        || extraMatchers.some((m) => rule[m] != null && (!Array.isArray(rule[m]) || rule[m].length));
      if (!hasMatcher && !rule.required) {
        setMsg(`❌ Rule ${i + 1} (${ruleKey}): needs a pattern, values${extraMatchers.length ? ', a range' : ''} or Required — otherwise it checks nothing`);
        return;
      }
    }

    try {
      setSaving(true);
      setMsg('Saving…');
      // buildPackSavePayload keeps identity.metaAppIds and drops the server-owned metaPlacementReport.
      const updatedPack = buildPackSavePayload(selectedPack, field, rules);
      const res = await fetch(`${proxyApiUrl}/proxy/apps/${selectedSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedPack),
      });
      if (!res.ok) throw new Error(res.status);
      const j = await res.json();
      setSelectedPack(j);
      setRules(JSON.parse(JSON.stringify((j.app || j)[field] || [])));
      // The pack store memoizes its initial fetch, so without this every other
      // consumer (getPack/findPackByIu and any subscriber) keeps serving the
      // PRE-save pack until a full reload — this editor showing one thing and
      // the rest of the app another.
      await refreshAppPacks();
      setMsg('✅ Saved');
      setTimeout(() => setMsg(''), 3500);
    } catch (e) {
      setMsg(`❌ Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const SEVERITY_OPTIONS = [
    { id: 'fail', label: 'Fail', color: '#c62828' },
    { id: 'warn', label: 'Warn', color: '#f57c00' },
    { id: 'info', label: 'Info', color: '#1976d2' },
  ];

  const PLATFORM_OPTIONS = [
    { id: 'iosmob', label: 'iOS Mobile' },
    { id: 'droidmob', label: 'Android Mobile' },
    { id: 'iostab', label: 'iOS Tablet' },
    { id: 'droidtab', label: 'Android Tablet' },
  ];

  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title={`${icon} ${title}`} badge={selectedPack ? `${rules.length} rule(s)` : ''} />
      <div style={SECTION_BODY}>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 14 }}>
          {blurb}
        </p>

        {showCreate && (
        <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--line, var(--line))' }}>
          <label style={FIELD_LABEL}>New app pack</label>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
            Packs are matched to traffic by network ID and brand code — the first two
            segments of the ad unit path. For <code>/4145/fnc/droidmob/hp/ban1</code> that
            is network <code>4145</code>, brand <code>fnc</code>.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input
              value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
              placeholder="slug (e.g. fnc)" style={{ ...INPUT_STYLE, flex: '1 1 140px' }}
            />
            <input
              value={newNetworkId} onChange={(e) => setNewNetworkId(e.target.value)}
              placeholder="network ID (e.g. 4145)" style={{ ...INPUT_STYLE, flex: '1 1 150px' }}
            />
            <input
              value={newBrands} onChange={(e) => setNewBrands(e.target.value)}
              placeholder="brand codes, comma separated" style={{ ...INPUT_STYLE, flex: '2 1 220px' }}
            />
            <button type="button" onClick={createPack} disabled={creating} style={{ ...INPUT_STYLE, cursor: 'pointer' }}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
        )}

        {!selectedSlug && packEntries.length === 0 && (
          <div style={{ padding: '16px 0', color: 'var(--muted)', fontSize: 13 }}>
            No app packs yet — create one under GAM Param Rules and its rules become editable here.
          </div>
        )}

        {packEntries.length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={FIELD_LABEL}>App Pack</label>
              <select
                value={selectedSlug || ''}
                onChange={e => setSelectedSlug(e.target.value || null)}
                style={{
                  ...INPUT_STYLE,
                  width: '100%',
                  minWidth: 200,
                }}
              >
                <option value="">← Select a pack</option>
                {packEntries.map(([slug, pack]) => (
                  <option key={slug} value={slug}>
                    {pack?.identity?.networkId ? `${pack.identity.networkId} / ${pack.identity.brandCodes?.join(', ')}` : slug} ({slug})
                  </option>
                ))}
              </select>
            </div>

            {selectedPack && (
              <>
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>Rules</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={prefill}
                      disabled={prefilling}
                      title="Derive rules from this pack's app profile — APS slot UUIDs, bundle ID, ad unit grammar"
                      style={{
                        padding: '5px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--line)',
                        background: 'var(--bg)',
                        color: 'var(--ink)',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: prefilling ? 'default' : 'pointer',
                        opacity: prefilling ? 0.6 : 1,
                      }}
                    >
                      {prefilling ? 'Deriving…' : 'Pre-fill from profile'}
                    </button>
                    <button
                      onClick={addRule}
                      style={{
                        padding: '5px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--line)',
                        background: 'var(--bg)',
                        color: 'var(--ink)',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      + Rule
                    </button>
                    </div>
                  </div>

                  {keyHelp && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>{keyHelp}</div>
                  )}

                  {rules.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>
                      No rules yet. Add one, or pre-fill from this pack's app profile.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {rules.map((rule, idx) => {
                        const patternError = rule.pattern ? validatePattern(rule.pattern).error : null;
                        return (
                          <div
                            key={idx}
                            style={{
                              padding: 12,
                              border: '1px solid var(--line)',
                              borderRadius: 6,
                              background: 'var(--bg)',
                              opacity: rule.derived ? 0.7 : 1,
                            }}
                          >
                            {rule.derived && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#1976d2',
                                  marginBottom: 8,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                }}
                              >
                                 Derived (edit to override)
                              </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: showPlatform ? '120px 1fr 1fr 100px 120px' : '1fr 1fr 1fr 100px', gap: 8, marginBottom: 8 }}>
                              <div>
                                <label style={FIELD_LABEL}>{keyLabel} *</label>
                                <input
                                  value={rule[keyField] || ''}
                                  onChange={e => updateRule(idx, keyField, e.target.value)}
                                  placeholder={keyPlaceholder.key}
                                  style={{ ...INPUT_STYLE, fontSize: 12 }}
                                />
                              </div>
                              <div>
                                <label style={FIELD_LABEL}>Label</label>
                                <input
                                  value={rule.label || ''}
                                  onChange={e => updateRule(idx, 'label', e.target.value)}
                                  placeholder={keyPlaceholder.label}
                                  style={{ ...INPUT_STYLE, fontSize: 12 }}
                                />
                              </div>
                              <div>
                                <label style={FIELD_LABEL}>Severity</label>
                                <select
                                  value={rule.severity || 'warn'}
                                  onChange={e => updateRule(idx, 'severity', e.target.value)}
                                  style={{ ...INPUT_STYLE, fontSize: 12 }}
                                >
                                  {SEVERITY_OPTIONS.map(s => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label style={FIELD_LABEL}>Required</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                  <input
                                    type="checkbox"
                                    checked={rule.required || false}
                                    onChange={e => updateRule(idx, 'required', e.target.checked)}
                                  />
                                  Yes
                                </label>
                              </div>
                              {showPlatform && (
                                <div>
                                  <label style={FIELD_LABEL}>Platform</label>
                                  <select
                                    value={rule.platform || 'all'}
                                    onChange={e => updateRule(idx, 'platform', e.target.value === 'all' ? null : e.target.value)}
                                    style={{ ...INPUT_STYLE, fontSize: 12 }}
                                  >
                                    <option value="all">All platforms</option>
                                    {PLATFORM_OPTIONS.map(p => (
                                      <option key={p.id} value={p.id}>{p.label}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>

                            {rule.mappings ? (
                              /* A mappings rule carries a whole gpid -> stored-request-id map,
                                 derived from the pack's own prebidMappings. Showing empty
                                 pattern/values inputs beside it would invite someone to type a
                                 matcher that the evaluator ignores. */
                              <div style={{ fontSize: 12, color: 'var(--muted)', padding: '6px 0' }}>
                                Matches each impression against{' '}
                                <strong>{Object.keys(rule.mappings).length}</strong> slot mapping(s)
                                from this pack's app profile. A slot with no mapping is not judged.
                              </div>
                            ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: extraMatchers.includes('range') ? '1fr 1fr 140px' : '1fr 1fr', gap: 8 }}>
                              <div>
                                <label style={FIELD_LABEL}>Pattern (regex)</label>
                                <input
                                  value={rule.pattern || ''}
                                  onChange={e => updateRule(idx, 'pattern', e.target.value)}
                                  placeholder={rule.values ? '(disabled — values set)' : patternPlaceholder}
                                  disabled={!!rule.values}
                                  style={{
                                    ...INPUT_STYLE,
                                    fontSize: 12,
                                    borderColor: patternError ? '#c62828' : 'var(--line)',
                                  }}
                                />
                                {patternError && (
                                  <div style={{ fontSize: 11, color: '#c62828', marginTop: 4 }}>
                                    {patternError}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label style={FIELD_LABEL}>Values (comma-separated)</label>
                                <input
                                  value={rule.values ? rule.values.join(', ') : ''}
                                  onChange={e => {
                                    const vals = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
                                    updateRule(idx, 'values', vals.length > 0 ? vals : null);
                                  }}
                                  placeholder={rule.pattern ? '(disabled — pattern set)' : valuesPlaceholder}
                                  disabled={!!rule.pattern}
                                  style={{
                                    ...INPUT_STYLE,
                                    fontSize: 12,
                                  }}
                                />
                              </div>
                              {extraMatchers.includes('range') && (
                                <div>
                                  <label style={FIELD_LABEL}>Range (min, max)</label>
                                  <input
                                    value={Array.isArray(rule.range) ? rule.range.join(', ') : ''}
                                    onChange={e => {
                                      const parts = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
                                      const nums = parts.map(Number);
                                      // Only a well-formed pair is stored: a half-typed range
                                      // would be dropped by sanitizePack anyway, and a rule that
                                      // silently loses its matcher is a rule that cannot fail.
                                      updateRule(idx, 'range', parts.length === 2 && nums.every(Number.isFinite) ? nums : null);
                                    }}
                                    placeholder="500, 3000"
                                    disabled={!!rule.pattern || !!rule.values}
                                    style={{ ...INPUT_STYLE, fontSize: 12 }}
                                  />
                                </div>
                              )}
                            </div>
                            )}

                            <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => removeRule(idx)}
                                style={{
                                  padding: '4px 10px',
                                  borderRadius: 6,
                                  border: '1px solid #c6282833',
                                  background: 'var(--bg)',
                                  color: '#c62828',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                  <button
                    onClick={save}
                    disabled={saving}
                    style={{
                      padding: '7px 18px',
                      borderRadius: 6,
                      border: 'none',
                      background: saving ? 'var(--muted)' : 'var(--primary)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: saving ? 'default' : 'pointer',
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    Save rules
                  </button>
                  {msg && <span style={{ fontSize: 12, color: msg.includes('✅') ? '#388e3c' : 'var(--muted)' }}>{msg}</span>}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AndroidProdCaptureSection() {
  return (
    <div style={SECTION_STYLE}>
      <SectionHeader title="📱 Device capture" />
      <div style={SECTION_BODY}>
        <DeviceCapturePanel />
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

/* ── App Pack screen expectations ─────────────────────────────────────────────
 * "Derive screens from this session": posts the session's rows to the proxy, shows the
 * proposed screens[] beside the pack's current ones, and Accept re-posts with accept:true.
 * Per-screen expectations drive the baseline-screen-* checks (docs/validation-autopilot-spec.md WP-A5). */
function ScreenList({ screens, testId }) {
  if (!screens || !screens.length) return <div style={{ fontSize: 12, color: 'var(--muted)' }}>None</div>;
  return (
    <ul data-testid={testId} style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {screens.map((sc, i) => (
        <li key={`${sc.id || sc.screen}-${i}`} style={{ fontSize: 12, color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, padding: '6px 8px', background: 'var(--bg)' }}>
          <strong>{sc.screen}</strong>{sc.platform ? ` (${sc.platform})` : ''}
          <div style={{ color: 'var(--muted)' }}>
            {(sc.slots || []).length} slot(s){(sc.bidders || []).length ? `, ${(sc.bidders || []).length} bidder(s)` : ''}, p50 {sc.requestP50 ?? 0} req
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ScreenExpectationsSection() {
  const { liveTrafficGroups } = useTraffic();
  const sessionRows = useMemo(() => Object.values(liveTrafficGroups || {}).flat(), [liveTrafficGroups]);
  const [packsVersion, setPacksVersion] = useState(0);
  useEffect(() => subscribeAppPacks(() => setPacksVersion((v) => v + 1)), []);
  const packEntries = useMemo(() => Object.entries(getPacks() || {}).sort((a, b) => a[0].localeCompare(b[0])), [packsVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const [slug, setSlug] = useState('');
  const [build, setBuild] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const derive = async () => {
    if (!slug) { setMsg('Select a pack first.'); return; }
    if (!sessionRows.length) { setMsg('No captured traffic in this session — capture on the Dashboard first.'); return; }
    setBusy(true); setMsg('');
    try { setResult(await deriveScreens(slug, { rows: sessionRows, build: build.trim() || undefined })); }
    catch (e) { setResult(null); setMsg(`Derive failed: ${e.message}`); }
    finally { setBusy(false); }
  };
  const accept = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await deriveScreens(slug, { rows: sessionRows, build: build.trim() || undefined, accept: true });
      await refreshAppPacks();
      setResult({ ...result, accepted: true, current: r.screens || [] });
      setMsg('Saved — screens updated.');
    } catch (e) { setMsg(`Accept failed: ${e.message}`); }
    finally { setBusy(false); }
  };
  const BTN = { padding: '5px 12px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' };

  return (
    <div style={SECTION_STYLE} data-testid="screen-expectations">
      <div style={SECTION_HEAD}><span style={{ fontWeight: 700, fontSize: 14 }}>Screen expectations</span></div>
      <div style={SECTION_BODY}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
          Per-screen slots, bidders and request counts the baseline-screen-* checks hold each build to. Derive them from a known-good session, review, then accept.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <select aria-label="App Pack" value={slug} onChange={(e) => { setSlug(e.target.value); setResult(null); setMsg(''); }} style={{ ...INPUT_STYLE, minWidth: 180 }}>
            <option value="">Select a pack</option>
            {packEntries.map(([s]) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input aria-label="Build" value={build} onChange={(e) => setBuild(e.target.value)} placeholder="build (optional)" style={{ ...INPUT_STYLE, width: 140 }} />
          <button type="button" onClick={derive} disabled={busy} style={{ ...BTN, opacity: busy ? 0.6 : 1 }}>Derive screens from this session</button>
        </div>
        {msg && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }} role="status">{msg}</div>}
        {result && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={FIELD_LABEL}>Current screens</label><ScreenList screens={result.current} testId="screens-current" /></div>
            <div>
              <label style={FIELD_LABEL}>{result.accepted ? 'Accepted' : 'Proposed screens'}</label>
              <ScreenList screens={result.proposal && result.proposal.screens} testId="screens-proposed" />
              {!result.accepted && (result.proposal?.screens || []).length > 0 && (
                <button type="button" onClick={accept} disabled={busy} style={{ ...BTN, marginTop: 8 }}>Accept</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Meta placement report (App Packs) ────────────────────────────────────────
 * Picks a pack and mounts MetaPlacementReportPanel. The report lives on the pack but is written
 * only by the import route; the rules editors' Save never sends it (utils/appPackPayload.js). */
function MetaPlacementsSection() {
  const [packsVersion, setPacksVersion] = useState(0);
  useEffect(() => subscribeAppPacks(() => setPacksVersion((v) => v + 1)), []);
  const packEntries = useMemo(() => Object.entries(getPacks() || {}).sort((a, b) => a[0].localeCompare(b[0])), [packsVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const [slug, setSlug] = useState('');
  return (
    <div style={SECTION_STYLE} data-testid="meta-placements-section">
      <div style={SECTION_HEAD}><span style={{ fontWeight: 700, fontSize: 14 }}>Meta placements (App Packs)</span></div>
      <div style={SECTION_BODY}>
        <select aria-label="App Pack for Meta placements" value={slug} onChange={(e) => setSlug(e.target.value)} style={{ ...INPUT_STYLE, minWidth: 180 }}>
          <option value="">Select a pack</option>
          {packEntries.map(([s]) => <option key={s} value={s}>{s}</option>)}
        </select>
        <MetaPlacementReportPanel slug={slug} nowMs={Date.now()} onImported={() => { refreshAppPacks(); }} />
      </div>
    </div>
  );
}

export default function ConfigPage() {
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>⚙️ Validator Config</h1>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
          Initialize an app profile once — the tool captures Prebid IDs, Amazon slots, banner sizes,
          and IU taxonomy automatically from live traffic.
          Without a profile, all traffic is validated against standard IAB/GAM structural rules.
        </p>
      </div>

      <AppProfileSection />
      <SslProxyingSection />
      <AndroidProdCaptureSection />
      <IvtChecksSection />
      <VideoMonitorSection />
      <ScanPlaylistsSection />
      <ScreenExpectationsSection />
      <MetaPlacementsSection />
      <ParamRulesSection
        field="gamParamRules"
        icon="🎯"
        title="GAM Param Rules"
        showCreate
        blurb="Define expected GAM ad-tag parameter values per business unit. Rules are evaluated for every GAM request and surfaced in the Checks detail view alongside spec-layer conformance."
        keyHelp={<>Keys are GAM ad-tag query params — <code>iu</code>, <code>msid</code>, <code>ppid</code>, <code>us_privacy</code>.</>}
        keyPlaceholder={{ key: 'iu, msid, etc.', label: 'Ad unit path' }}
        patternPlaceholder="^/4145/..."
        valuesPlaceholder="1YNN, 1YNY, 1---"
      />
      <ParamRulesSection
        field="apsParamRules"
        icon="📦"
        title="APS SDK Field Rules"
        blurb="Define expected Amazon APS values per business unit — above all the slot UUIDs this app registered in the APS console, since a staging UUID in a production build causes silent no-fill rather than an error. Evaluated for every APS mobile-SDK display request."
        keyHelp={(
          <>
            Keys are dot-paths into the APS request body, not query params:{' '}
            <code>slots.slot</code>, <code>pkg.pn</code>, <code>pj.cstm.cturl</code>, <code>appId</code>.
            Use dot-form only — <code>slots[0].slot</code> resolves to nothing and the rule
            silently passes. Set Platform on iOS-only fields (<code>pj.att</code>,{' '}
            <code>skadn</code>, <code>pj.api</code>), or they match nothing on Android and read
            as a pass.
          </>
        )}
        keyPlaceholder={{ key: 'slots.slot, pkg.pn, etc.', label: 'APS slot UUID' }}
        patternPlaceholder="^https://(www\.)?example\.com"
        valuesPlaceholder="b19838b2-bb3c-4e08-a3a4-6d7c7e016663"
      />
      <ParamRulesSection
        field="prebidParamRules"
        icon="🅿️"
        title="Prebid ORTB Field Rules"
        keyField="path"
        keyLabel="Path"
        showPlatform={false}
        extraMatchers={['range', 'mappings']}
        blurb="Define expected Prebid auction values per business unit — above all which stored request id belongs to which slot, since a wrong one means Prebid Server resolves no bidders and the auction returns nothing. Evaluated for every Prebid Mobile → Prebid Server request."
        keyHelp={(
          <>
            Paths are dot-paths into the OpenRTB request body, not query params:{' '}
            <code>app.bundle</code>, <code>ext.prebid.storedrequest.id</code>,{' '}
            <code>imp.ext.prebid.storedrequest.id</code>, <code>tmax</code>. A path under{' '}
            <code>imp.</code> is judged per impression, so one wrong slot in a four-slot
            auction is reported as that slot. There is no Platform column: an ORTB body
            carries nothing that says which build sent it — use Pre-fill, which unions your
            profiles instead.
          </>
        )}
        keyPlaceholder={{ key: 'app.bundle, tmax, etc.', label: 'App bundle' }}
        patternPlaceholder="^com\\.example\\."
        valuesPlaceholder="com.example.generic"
      />
      <AutomationSection />
    </div>
  );
}
